import { Component, OnInit, Input, OnDestroy, ChangeDetectorRef, OnChanges } from '@angular/core';
import { MeteringPoint, 
  Customer, 
  MeteringSubFilter, 
  UserSettings, 
  AnalyzePaneSettings, 
  InformationGroup, 
  ApplicationConfiguration, 
  MeteringPointStub, 
  UserInfo, 
  Contract, 
  AnalyzePeriodData,
  TaxReturnAgreementParams} from '@model';
import { MeteringType, MeteringFilterType,
   AnalyzeSettingsType, 
   UserType, 
   AnalyzeUnitType, 
   AnalyzeConsumptionType, 
   DatePeriodType, 
   AdditionCodes, 
   PermissionType} from '@enums';
import { SxSnackbarService, SxDialogService, SxPermissionService, SxExportService } from '@services';
import { SubSink } from 'subsink';
import { filter, take, map, tap, switchMap, combineLatestWith } from 'rxjs/operators';
import * as fromStore from '@core/store';
import { MeteringPointService, AgreementService } from '@appStore/metering-points/services';
import { TranslateService } from '@ngx-translate/core';
import { Router } from '@angular/router';
import * as fromAccountStore from '@appStore/account';
import * as fromMeteringStore from '@appStore/metering-points';
import * as fromAnalyzeStore from '@appStore/analyze';
import * as fromContractStore from '@appStore/contracts';
import { Store } from '@ngrx/store';
import * as combinedSelectors from '@appStore/account/combined-selectors';
import { MeteringGroupFormComponent } from '../metering-group-form/metering-group-form.component';
import { Observable, combineLatest } from 'rxjs';
import * as fromConfiguration from '@appStore/configuration';
import { InformationKey } from 'app/models/enums/information-key.enum';
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import { ToLocalDateTimePipe } from '@utils/pipes';
import { BusinessArea } from 'app/models/enums/business-area.enum';

@Component({
    selector: 'app-metering-point-item-pane',
    templateUrl: './metering-point-item-pane.component.html',
    standalone: false
})
export class MeteringPointItemPaneComponent implements OnInit, OnDestroy, OnChanges {
  @Input()
  meteringPoint: MeteringPoint;

  @Input()
  meteringPoints: MeteringPoint[];

  @Input()
  customer: Customer;

  @Input()
  userInfo: UserInfo;

  @Input()
  filterArea: string;

  @Input()
  userType: UserType;

  @Input()
  contract: Contract;

  @Input()
  periodData$: Observable<AnalyzePeriodData>;

  @Input()
  isWhiteLabel: boolean;

  MeteringTypeEnum = MeteringType;

  userSettings: UserSettings;
  meteringPointsCount = 0;
  hasManageGroupPermission = true;
  hasManageAlarmsPermission = true;
  hasIntelligy = false;
  taxReturingMeteringPoint = false;
  taxReturingMeteringPointEnabled = false;
  isPrivate = false;
  ageOfCentralInYears = 0;
  serviceAgreementsEnabled = false;

  matTooltipText: string;
  intelligyIcon = "#sx-intelligy";

  private subs = new SubSink();
  exportRequested = false;
  informationLoaded$: Observable<boolean>;
  meteringpointInformationGroup: InformationGroup =  {
    id : 0,
    informations: [],
    expanded: true
  };
  intelligyStateLoaded$: Observable<boolean>;
  appConfig: ApplicationConfiguration;
  clientIsIntelligyCompatible = false;
  customerServiceEmailAddress: string;
  meteringPointName: string;

  co2CompensationKeyFigure: number;
  co2CompensationCertificateYear: number;
  co2CompensationCalculated = false;
  co2CompensationInTons = 0;
  hasClimateNeutralAddition = false;
  isProcessing = false;

  currentPeriodData: AnalyzePeriodData;
  UserTypeEnum = UserType;
  insufficentPermissions: boolean;
  PermissionEnum = PermissionType;
  businessArea: BusinessArea;


  constructor(
    private meteringPointService: MeteringPointService,
    private agreementService: AgreementService,
    private translate: TranslateService,
    private sxSnackbarService: SxSnackbarService,
    private router: Router,
    private accountStore: Store<fromAccountStore.State>,
    private meteringPointStore: Store<fromMeteringStore.State>,
    private analyzeStore: Store<fromAnalyzeStore.State>,
    private contractStore: Store<fromContractStore.State>,
    private sxDialogService: SxDialogService,
    private changeDetectorRef: ChangeDetectorRef,
    private sxPermissionService: SxPermissionService,
    private exportService: SxExportService,
    private store: Store<fromStore.InformationState>,
    private toLocalDate: ToLocalDateTimePipe,
    private configStore: Store<fromConfiguration.State>
  ) {
    this.co2CompensationCalculated = false;
    this.hasClimateNeutralAddition = false;
    this.isProcessing = false;
  }

  get isManager(){
    return this.userType === this.UserTypeEnum.Manager;
  }

  ngOnInit() {
    dayjs.extend(duration);

    this.subs.sink = this.accountStore
      .select(combinedSelectors.getFilledSettings)
      .pipe(
        filter(x => !!x),
        take(1)
      )
      .subscribe(x => (this.userSettings = x));

      this.subs.sink = this.configStore.select(fromConfiguration.getApiConfiguration)
      .pipe(
        combineLatestWith(this.configStore.select(fromConfiguration.getActiveClientId))
      )
      .subscribe(([apiConfig, activeClientId]) => {
        this.appConfig = apiConfig;
        this.taxReturingMeteringPointEnabled = this.appConfig?.industiralTaxEnabled;
        let taxReturingMeteringPoints = this.appConfig?.industiralTaxMeteringPoints.split(';');
        this.taxReturingMeteringPoint = taxReturingMeteringPoints.some(mp => mp === this.meteringPoint.id.toString());
        this.clientIsIntelligyCompatible = this.appConfig?.intelligyCompatibleClients.includes(activeClientId);
        this.customerServiceEmailAddress = this.appConfig?.comCustomerServiceEmailAddress;
        this.co2CompensationCertificateYear = this.appConfig?.co2CompensationCertificateYear;
        this.co2CompensationKeyFigure = this.appConfig?.co2CompensationKeyFigure;
        this.serviceAgreementsEnabled = this.appConfig?.serviceAgreementsEnabled;
        if(!this.meteringPoint.externalOrganisationId) {
          // Get contract addition for this meteringPoint if it is not an external organisation
          this.getContractAddition();
        }
      });

      this.insufficentPermissions = this.sxPermissionService.securityTrim(
        undefined,
        [PermissionType.SignServices],
        this.contract?.customerId,
        undefined
      ) ? false : true;

      // Fetch HasIntelligy from MeteringPoint instead...
      this.hasIntelligy = this.meteringPoint.hasIntelligy;
      
      const meteringPointInformations$ = this.store.select(fromStore.getMeteringPointInformations);
      this.subs.sink = meteringPointInformations$.subscribe(mInfo => {
        if (mInfo) {
          this.meteringpointInformationGroup = {
            id : 0,
            informations: this.meteringPoint.hasIntelligy ?
            mInfo.filter(x => x.key === InformationKey.MeteringpointInfoGeneric)
              : mInfo.filter(x => x.key === InformationKey.MeteringpointInfoIntelligy),
            expanded: true
          }
        }
      });

      if (this.meteringPoint && this.meteringPoints && this.meteringPoints.length > 0) {

        this.businessArea = this.meteringPoint.meteringType === MeteringType.Hot || 
          this.meteringPoint.meteringType === MeteringType.HotLogical ? BusinessArea.Hot : BusinessArea.Cold;

        this.meteringPointsCount = this.meteringPoints.filter(
          x => x.meteringType === this.meteringPoint.meteringType
        ).length;

        if (this.meteringPointsCount > 1) {
          this.changeDetectorRef.detectChanges();
        }
      }

      this.subs.sink = this.periodData$.subscribe(periodData => {
        if (periodData && periodData?.consumptionData) {
          this.currentPeriodData = periodData;
        }
      });

    this.hasManageGroupPermission = this.sxPermissionService.hasUserManageMeteringPointGroupsPermission();
    this.hasManageAlarmsPermission = this.sxPermissionService.hasUserManageAlarmsPermission();
    this.isPrivate = this.userType === UserType.Private;
    this.meteringPointName = this.capitalize(this.meteringPoint.name);
    this.ageOfCentralInYears = this.calculateAge(this.meteringPoint);

    this.matTooltipText = !!this.userInfo.impersonatedAsName ? 
      this.translate.instant('metering.item.overview.intelligy-impersonation-tooltip')
      : null;
   
    this.changeDetectorRef.detectChanges();
  }

  // Ålder på fjärrkyla-/fjärrvärmecentral
  calculateAge(meteringPoint: MeteringPoint): number {
 
    let years = 0;

    let latestConstructionDateStr = meteringPoint.latestConstructionDate;
    if (!latestConstructionDateStr) {
      return 0;
    }
    //console.log('latestConstructionDateStr: ' + latestConstructionDateStr);

    let latestConstructionDate =  dayjs(latestConstructionDateStr);
    //console.log('latestConstructionDate: ' + latestConstructionDate);
  
    // First in Month (Used in FortumServices)
    let toDate = dayjs().startOf('month');
    //console.log('toDate: ' + toDate);

    let duration = dayjs.duration(toDate.diff(latestConstructionDate));
    let numberOfdays = duration.asDays();
    //console.log('numberOfdays: ' + numberOfdays);

    
    // numberofDays / 365.25 => to Integer
    years = Math.round(numberOfdays / 365.25);
    //console.log('years: ' + years);
    return years;
  }

  ngOnChanges() {
  }

  getContractAddition() {

    // Get co2CompensationCertificateDate interval
    let co2CompensationCertificateDateFrom = dayjs(new Date(this.co2CompensationCertificateYear, 0, 1))
      .startOf('day')
      .toDate();

    let co2CompensationCertificateDateTo = dayjs(new Date(this.co2CompensationCertificateYear, 11, 31))
      .endOf('day')
      .toDate();

      // Check if the Customer/MeteringPoint had the contractAddition during the date-intervall?
    let additionCodes = [];
    additionCodes.push(AdditionCodes.ClimateNeutral);
    this.store.dispatch(new fromContractStore.HasContractAdditions({
          meteringPointId: this.meteringPoint.id,
          customerId: this.meteringPoint.customerId,
          from: co2CompensationCertificateDateFrom,
          to: co2CompensationCertificateDateTo,
          additions: additionCodes
        }));

    this.subs.sink = this.store
          .select(fromContractStore.getHasContractAdditionsLoaded)
          .pipe(
            filter(x => !!x),
            take(1),
            switchMap(x => this.store.select(fromContractStore.getHasContractAdditions).pipe(take(1)))
          )
          .subscribe(hasAdditions => {
            if (hasAdditions) {
              this.isProcessing = true;
              this.hasClimateNeutralAddition = true;
              this.co2CompensationCalculated = false;

              let dateFrom = co2CompensationCertificateDateFrom;
              let dateTo = co2CompensationCertificateDateTo;

              let unit = this.userType === UserType.Private ? AnalyzeUnitType.Kwh : AnalyzeUnitType.Mwh;

              let meteringType =  this.meteringPoint.meteringType === MeteringType.Hot
                  ? this.meteringPoint.type === 'LOG' ? MeteringType.HotLogical : MeteringType.Hot
                  : this.meteringPoint.type === 'LOG' ? MeteringType.ColdLogical : MeteringType.Cold;

              this.subs.sink = this.meteringPointStore
                .select(fromMeteringStore.getClimateCompensatedCO2)
                  .pipe(take(1))
                  .subscribe((i) => {
                  if (!i || i.meteringPointId !== this.meteringPoint.id) {
                    // Climate compensation for meteringPoint not in Store
                 
                    this.meteringPointStore.dispatch(
                      new fromMeteringStore.LoadClimateCompensatedCO2({
                        meteringPointId: this.meteringPoint.id,
                        customerId: this.meteringPoint.customerId,
                        from: dateFrom,
                        to: dateTo,
                        resolution: DatePeriodType.Day,
                        type: AnalyzeConsumptionType.Energy,
                        unit: unit,
                        meteringType: meteringType,
                        compensationKeyFigure: this.co2CompensationKeyFigure
                      })
                    );
                    this.subs.sink = this.meteringPointStore
                      .select(fromMeteringStore.getClimateCompensatedCO2Loaded)
                      .pipe(
                        filter(x => !!x),
                        take(1),
                        switchMap(x => this.store.select(fromMeteringStore.getClimateCompensatedCO2).pipe(take(1)))
                    )
                    .subscribe(x => {
                      // Fetched climate compensation from backend
                      this.co2CompensationInTons = Math.round(x.compensation * 10) / 10;
                      this.isProcessing = false;
                      this.co2CompensationCalculated = true;
                    });
                  } else {
                    // Climate compensation in Store
                    this.co2CompensationInTons =  Math.round(i.compensation * 10) / 10;
                    this.isProcessing = false;
                    this.co2CompensationCalculated = true;
                  }
              });
            } else {
              // No additions
              this.isProcessing = false;
              this.hasClimateNeutralAddition = false;
              this.co2CompensationCalculated = true;
            }
      });
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
    // Clear state
    this.meteringPointStore.dispatch(new fromMeteringStore.ClearClimateCompensatedCO2State());
  }

  goToIntelligyPage() {
    this.router.navigate([`/secure/intelligy`]);
  }

  goToIntelligyApplication() {
    if (!!this.userInfo.impersonatedAsName) return;
    
    this.accountStore.dispatch(new fromAccountStore.SetIntelligyLogin());
    window.open(this.appConfig?.intelligyLoginUrl, '_self');
  }

  goToAgreement = () => {
    this.router.navigate([`/secure/contracts/redirect/${this.meteringPoint.id}`], { skipLocationChange: true });
  };

  goToServiceAgreements = () => {

    // Set selected Tab to service agreement
    this.router.navigate([`/secure/products/serviceagreements/redirect/${this.meteringPoint.id}`], { skipLocationChange: true });
  };

  changeArea = () => {
    alert('mailto: ' + this.customerServiceEmailAddress);
  };

  goToInvoices = () => {
    if (!this.userSettings) {
      return;
    }
    this.setMainFilter();
    this.router.navigate([`/secure/invoices/${this.meteringPoint.id}`]);
  };

  goToAnalyze = () => {
    if (!this.userSettings) {
      return;
    }
    this.setMainFilter();

    let meteringType =  this.meteringPoint.meteringType === MeteringType.Hot
      ? this.meteringPoint.type === 'LOG' ? MeteringType.HotLogical : MeteringType.Hot
      : this.meteringPoint.type === 'LOG' ? MeteringType.ColdLogical : MeteringType.Cold;
    let analyzeSettings = {
      ...AnalyzePaneSettings.initalState(meteringType, AnalyzeSettingsType.Energy)
    };

    this.analyzeStore.dispatch(new fromAnalyzeStore.AnalyzeUpdateSettings(analyzeSettings));

    this.router.navigate([`/secure/analyze/${this.meteringPoint.id}`]);
  };

  goToAlarm = () => {
    this.router.navigate([`/secure/alarms/redirect/${this.meteringPoint.id}`], { skipLocationChange: true });
  };

  addToGroup = () => {
    let dialogRef = this.sxDialogService.openDialog(MeteringGroupFormComponent, {
      selectedMeteringPoints: [this.meteringPoint],
      meteringType: this.meteringPoint.meteringType
    });

    this.subs.sink = dialogRef.afterClosed().subscribe((result: string) => {
      if (!!result && result === 'save') {
        this.sxSnackbarService.openSnackBar(this.translate.instant('metering.item.overview.added-to-group'), '');
      }
    });
  };

  setMainFilter = () => {
    let meteringType =  this.meteringPoint.meteringType === MeteringType.Hot
      ? this.meteringPoint.type === 'LOG' ? MeteringType.HotLogical : MeteringType.Hot
      : this.meteringPoint.type === 'LOG' ? MeteringType.ColdLogical : MeteringType.Cold;

    let currentFilter = this.meteringPoint.meteringType === MeteringType.Hot
      ? this.meteringPoint.type === 'LOG' ? this.userSettings.hotLogicalMeteringFilter : this.userSettings.hotMeteringFilter
      : this.meteringPoint.type === 'LOG' ? this.userSettings.coldLogicalMeteringFilter : this.userSettings.coldMeteringFilter;

    let subFilter: MeteringSubFilter = null;
    if (meteringType === MeteringType.HotLogical || meteringType === MeteringType.ColdLogical) {
        subFilter = <MeteringSubFilter>{
          meteringType: meteringType,
          includedMeteringPoints: this.meteringPoint.containedMeteringPoints.map(
            mp => <MeteringPointStub>{ id: mp.id, name: mp.name, selected: true }
          )
      };
    }

    currentFilter = {
      ...currentFilter,
      filterType: MeteringFilterType.MeteringPoint,
      filterId: this.meteringPoint.id,
      meteringType: meteringType,
      name: this.meteringPoint.name,
      containsMeteringPointIds: this.meteringPoint.containedMeteringPoints
        ? this.meteringPoint.containedMeteringPoints.map(x => x.id)
        : [],
      subfilter: subFilter,
    };

    this.accountStore.dispatch(
      new fromAccountStore.FilterSettingUpdate({
        meteringType: meteringType,
        filter: currentFilter,
        userId: this.userInfo.id,
        filterArea: this.filterArea,
        meteringPoints: this.meteringPoints })
    );
  };

  getReport(meteringPointId: number) {
    let type = this.meteringPoint.meteringType === MeteringType.Hot ? 'Fjärrvärmerapport' : 'Fjärrkylarapport';
    let meteringPointName = this.meteringPoint.name;
    let filename = type + '-' + meteringPointId + '-' + this.capitalize(this.replaceInvalidChars(meteringPointName));
    let timestamp = this.toLocalDate.transform(new Date(), 'YYYY-MM-dd');

    this.subs.sink = this.exportService.exportInNewWindow(
      'meteringpoints.info.downloading_report',
      this.meteringPointService.createReportKey(),
      `/meteringpoints/${meteringPointId}/summaryreport/${filename}_${timestamp}`
    );
  }

  capitalize = s => {
    if (typeof s !== 'string') {
      return '';
    }
    return s.charAt(0).toUpperCase() + s.slice(1);
  };

  replaceInvalidChars = s => {
    return s.replace(/[/\\?%*:|"<>+]/g, '-');
  };

  displayAgreementSignPage = () => {
     // prevent multiple clicks
     if (this.exportRequested) {
      return;
    }

    this.exportRequested = true;
    let request: TaxReturnAgreementParams = { meteringPointId: this.meteringPoint.id.toString() };

    this.meteringPointStore.dispatch(new fromMeteringStore.CreateTaxReturnAgreement(request));
    this.subs.sink = this.contractStore.select(fromMeteringStore.getTaxReturnAgreementCreated)
      .pipe(
        filter(created => created),
        take(1)
      ).subscribe(isCreated => {
        this.contractStore.select(fromMeteringStore.getTaxReturnAgreementCreatedId)
          .pipe(
            take(1)
          ).subscribe(agreementId => {
          
            this.contractStore.dispatch(new fromMeteringStore.AgreementLinkLoad(agreementId));
            this.contractStore.select(fromMeteringStore.getActiveAgreementLinkLoaded)
            .pipe(
              filter(loaded => loaded),
              take(1)
            ).subscribe(() => {
              this.exportRequested = false;
              this.changeDetectorRef.detectChanges();
              this.subs.sink = this.exportService.openLinkInNewWindow('metering.item.overview.agreement_download',
                this.contractStore.select(fromMeteringStore.getActiveAgreementLink));
             
            });
        });
      });
  }
}
